import {ActionIcon, Button, NumberInput, Paper, Select, Tabs, Text} from "@mantine/core";
import {CurrencyDollarIcon, ArrowClockwiseIcon, TrashIcon} from "@phosphor-icons/react";
import {useCallback, useContext, useMemo, useState} from "react";
import Page from "../../components/Page/Page";
import api, {useUser} from "../../utils/api";
import {DataTable} from "mantine-datatable";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {Col, Row} from "../../components/Flex/Flex.tsx";
import MessageContext from "../../context/MessageContext.tsx";
import {usePaySubscribe} from "./usePaySubscribe.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import {bodyNumber} from "../../utils/formatters.ts";
import {bodyLicenseId, bodyRelativeTimestamp, bodyStatus, bodyTimestamp, IconNo, IconYes, transactionPayDateBody} from "./shared.tsx";
import {Duration} from "luxon";
import {User, Faction, License, LicenseTransaction} from "../../types/general.types.ts";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";

const getFactionMembers = async (id: string | undefined) => {
    if (id === undefined) {
        return [];
    }
    const res = await api.get('/faction/manage/users/' + id)
    return res.data.sort((a: User, b: User) => a.handle.localeCompare(b.handle))
}

const getFactionLicenses = async (id: string | undefined) => {
    if (id === undefined) {
        return {licenses: [] as License[], transactions: [] as LicenseTransaction[]};
    }
    const res = await api.get('/faction/manage/licenses/' + id)
    return res.data;
}

const bodyDuration  = (row: License) => {
    let interval;
    if(row.duration === 31536000) {
        interval = Duration.fromObject({years: 1});
    } else {
        interval = Duration.fromObject({seconds: row.duration},  { conversionAccuracy: 'longterm' }).shiftTo( 'days');
    }
    return interval.toHuman();
}

const INACTIVE_AFTER_SECONDS = 15 * 24 * 60 * 60;

const isInactive = (member: User) => {
    return !member.lastActive || Date.now() / 1000 - member.lastActive > INACTIVE_AFTER_SECONDS;
}

const TAB_PAYMENTS = "payments";
const TAB_SUBSCRIPTIONS = "subscriptions";
const TAB_MEMBERS = "members";

export default function FactionLicenses() {
    const {factions, user} = useUser();
    const [faction, setFaction] = useState<Faction | null>(null);
    const [buyQty, setBuyQty] = useState<number | null>(null);
    const {success, error} = useContext(MessageContext);
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useState(TAB_SUBSCRIPTIONS);

    const subscriptionsTableState = useDataTableSessionState({
        storageKey: 'subscriptions:faction:licenses',
        initialSortStatus: {columnAccessor: 'created', direction: 'desc' as const},
    });

    const {verifying, initializeTransaction, verifyTransaction, openPayment} = usePaySubscribe({
        onSuccess: () => {
            success('Payment has been verified', 'Thank for your support!');
            queryClient.invalidateQueries({queryKey: ['faction', 'licenses', faction?.id]});
            queryClient.invalidateQueries({queryKey: ['user']});
        },
        onError: (title, message) => {
            error(title, message);
        }
    })

    const possibleFactions = useMemo(() => {
        const options: Faction[] = [];
        factions.forEach(f => {
            if ((f.permissions && f.permissions.manageLicenses) || f.leader?.id === user.uid || f.second?.id === user.uid) {
                options.push(f);
            }
        });
        if (options.length === 1) {
            setFaction(options[0]);
        }
        return options;
    }, [factions, user.uid]);

    const factionSelectData = useMemo(() => {
        return possibleFactions.map(f => ({value: f.id, label: f.name}));
    }, [possibleFactions]);

    const {data: members} = useQuery<User[]>({
        queryKey: ['faction', 'members', faction?.id],
        queryFn: () => getFactionMembers(faction?.id)
    });

    const {data} = useQuery<{ licenses: License[], transactions: LicenseTransaction[] }>({
        queryKey: ['faction', 'licenses', faction?.id],
        queryFn: async () => {
            const data = await getFactionLicenses(faction?.id)
            if(data.transactions.some((t: LicenseTransaction) => !t.payDate)) {
                setActiveTab(TAB_PAYMENTS)
            }
            return data;
        }
    })

    const memberOptions = useMemo(() => {
        if (!members) {
            return [];
        }
        return members.filter(m => !(m.subLevel>0));
    }, [members]);

    const memberSelectData = useMemo(() => {
        return memberOptions.map(m => ({value: m.uid, label: m.handle}));
    }, [memberOptions]);

    const inactiveMembers = useMemo(() => {
        return new Set(memberOptions.filter(isInactive).map(m => m.uid));
    }, [memberOptions]);

    const {activeLicenses, availableLicenses} = useMemo(() => {
        if(!data) {
            return {activeLicenses: 0, availableLicenses: 0}
        }
        const activeLicenses = data.licenses.filter(l => l.status === 'active').length;
        const availableLicenses = data.licenses.filter(l => l.status === 'available').length;
        return {activeLicenses, availableLicenses}
    }, [data?.licenses]);

    const addNewLicenses = () => {
        initializeTransaction(`/subscribe/link/group/${faction?.id}/${buyQty}`)
    }


    const bodyTransactionActions = useCallback((row: LicenseTransaction) => {
        if (!row.payDate) {
            return (
                <div className="flex gap-2">
                    {row.link && (<Button leftSection={<CurrencyDollarIcon size={16}/>} size="compact-sm" variant="default" onClick={() => openPayment(row.id, row.link)} title="Pay Now">Pay</Button>)}
                    <Button leftSection={<ArrowClockwiseIcon size={16}/>} size="compact-sm" variant="default" onClick={() => verifyTransaction(row.id)} title="Verify Payment">Verify</Button>
                </div>
            )
        }
    }, [openPayment, verifyTransaction]);

    const bodyLicenseActions = useCallback((row: License) => {
        // Only show actions for available or active licenses
        if (row.status === 'available' || row.status === 'active') {
            return (
                <div className="flex gap-2">
                    {row.status === 'available' && memberOptions.length > 0 && (
                        <Select
                            data={memberSelectData}
                            placeholder="Assign to member"
                            renderOption={({option}) => inactiveMembers.has(option.value)
                                ? <Text size="sm" c="dimmed">{option.label} <Text span size="xs">(inactive)</Text></Text>
                                : option.label
                            }
                            onChange={async (value) => {
                                if (!value) return;
                                const member = memberOptions.find(m => m.uid === value);
                                try {
                                    await api.post(`/faction/manage/licenses/${faction?.id}/assign`, {
                                        license: row.id,
                                        target: value
                                    });
                                    queryClient.invalidateQueries({queryKey: ['faction', 'licenses', faction?.id]});
                                    queryClient.invalidateQueries({queryKey: ['faction', 'members', faction?.id]});

                                    success('License Assigned', `License assigned to ${member?.handle ?? value}`);
                                } catch (err) {
                                    error('Assignment Failed', 'Could not assign license');
                                }
                            }}
                            style={{width: '200px'}}
                        />
                    )}
                    {row.status === 'active' && (
                        <ActionIcon
                            variant="subtle"
                            onClick={async () => {
                                try {
                                    await api.post(`/faction/manage/licenses/${faction?.id}/remove`, {
                                        license: row.id
                                    });
                                    queryClient.invalidateQueries({queryKey: ['faction', 'licenses', faction?.id]});
                                    queryClient.invalidateQueries({queryKey: ['faction', 'members', faction?.id]});
                                    success('License Removed', 'License has been removed');
                                } catch (err) {
                                    error('Removal Failed', 'Could not remove license');
                                }
                            }}
                        >
                            <TrashIcon size={16} />
                        </ActionIcon>
                    )}
                </div>
            );
        }
        return null;
    }, [faction?.id, memberOptions, memberSelectData, inactiveMembers, queryClient, success, error]);

    const bodyIsFromFaction = useCallback((row: User) => {
        if(data?.licenses.some(l => l.id === row.subLicense)) {
            return <IconYes />;
        } else {
            return <IconNo />;
        }
    }, [data?.licenses]);
    const bodyIsSubscribed = useCallback((row: User) => {
        return row.subLevel > 0 ? <IconYes /> : <IconNo />
    }, []);

    // Sorted licenses
    const sortedLicenses = useMemo(() => {
        if (!data?.licenses) return [];
        const sorted = [...data.licenses].sort((a, b) => {
            const key = subscriptionsTableState.sortStatus.columnAccessor;
            const aVal = key.includes('.') ? key.split('.').reduce((o: any, k: string) => o?.[k], a) : (a as any)[key];
            const bVal = key.includes('.') ? key.split('.').reduce((o: any, k: string) => o?.[k], b) : (b as any)[key];
            if (typeof aVal === 'string') return aVal.localeCompare(bVal);
            return (aVal ?? 0) - (bVal ?? 0);
        });
        return subscriptionsTableState.sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
    }, [data?.licenses, subscriptionsTableState.sortStatus]);

    return (
        <Page>
            <title>Faction Licenses</title>
            <h1>Manage Faction Licenses</h1>
            {possibleFactions.length > 1 &&
                <Select
                    value={faction?.id ?? null}
                    data={factionSelectData}
                    placeholder="Select a faction"
                    onChange={value => {
                        const f = factions.find(f => f.id === value);
                        if (f) {
                            setFaction(f)
                        }
                    }}
                />
            }
            {verifying && <LoaderScreen message="Verifying transaction"/>}
            {faction && data &&
                <Paper className="mt-4" p="md">
                    <h3>{faction.name}</h3>
                    <Row>
                        <Col>Active Licenses: {activeLicenses}</Col>
                        <Col>Available Licenses: {availableLicenses}</Col>
                        <Col grow={1}/>
                        <Col>Buy more licenses: <NumberInput value={buyQty} onChange={setBuyQty} size="xs" min={1} style={{display: 'inline-block', width: 80}}/></Col>
                        <Col><Button leftSection={<CurrencyDollarIcon size={16}/>} onClick={addNewLicenses} disabled={!buyQty}>Purchase now</Button></Col>
                    </Row>

                    <Tabs value={activeTab} onChange={setActiveTab}>
                        <Tabs.List>
                            <Tabs.Tab value={TAB_PAYMENTS}>Payments</Tabs.Tab>
                            <Tabs.Tab value={TAB_SUBSCRIPTIONS}>Subscriptions</Tabs.Tab>
                            <Tabs.Tab value={TAB_MEMBERS}>Members</Tabs.Tab>
                        </Tabs.List>
                        <Tabs.Panel value={TAB_PAYMENTS}>
                            <DataTable
                                records={data.transactions}
                                columns={[
                                    {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'})},
                                    {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'})},
                                    {accessor: 'price', title: 'Total', render: (row) => bodyNumber(row, {field: 'price'})},
                                    {accessor: 'payDate', title: 'Paid', render: (row) => transactionPayDateBody(row)},
                                    {accessor: 'actions', title: 'Actions', render: (row) => bodyTransactionActions(row)},
                                ]}
                            />
                        </Tabs.Panel>
                        <Tabs.Panel value={TAB_SUBSCRIPTIONS}>
                            <DataTable
                                records={sortedLicenses}
                                sortStatus={subscriptionsTableState.sortStatus}
                                onSortStatusChange={subscriptionsTableState.setSortStatus}
                                columns={[
                                    {accessor: 'id', title: 'ID', render: (row) => bodyLicenseId(row, {field: 'id'})},
                                    {accessor: 'created', title: 'Created', render: (row) => bodyTimestamp(row, {field: 'created'})},
                                    {accessor: 'payDate', title: 'Pay Date', render: (row) => bodyTimestamp(row, {field: 'payDate'})},
                                    {accessor: 'expireDate', title: 'Valid until', render: (row) => bodyTimestamp(row, {field: 'expireDate'})},
                                    {accessor: 'duration', title: 'Duration', render: (row) => bodyDuration(row)},
                                    {accessor: 'status', title: 'Status', render: (row) => bodyStatus(row)},
                                    {accessor: 'targetUser.name', title: 'Assigned to'},
                                    {accessor: 'actions', title: 'Actions', render: (row) => bodyLicenseActions(row)},
                                ]}
                            />
                        </Tabs.Panel>
                        <Tabs.Panel value={TAB_MEMBERS}>
                            <DataTable
                                records={members ?? []}
                                idAccessor="uid"
                                columns={[
                                    {accessor: 'handle', title: 'User'},
                                    {accessor: 'subLevel', title: 'Subscribed', render: (row) => bodyIsSubscribed(row)},
                                    {accessor: 'subLicense', title: 'Is from faction', render: (row) => bodyIsFromFaction(row)},
                                    {accessor: 'lastActive', title: 'Last active', render: (row) => bodyRelativeTimestamp(row, {field: 'lastActive'})},
                                ]}
                            />
                        </Tabs.Panel>
                    </Tabs>

                </Paper>
            }
        </Page>
    )


}
